/**
 * Inventory Panel — Session 5 rework (spec 15).
 *
 * Features
 * --------
 * - Categories: All | Weapons | Armor | Trinkets | Consumables | Materials | Quest
 * - Search by name (case-insensitive substring)
 * - Sort: Rarity | Level | Item Power | Type | Newest | Value
 * - Detailed compare: stat deltas + elemental damage/resistance deltas
 * - Class Sets summary integrated at the top (replaces standalone Workshop panel)
 * - Uses <ElementBadge> from Session 2 for elemental affixes
 *
 * Server contract untouched: /api/inventory/equip and /api/inventory/use still
 * accept the same payload.
 */
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { localizedString } from "@/lib/localized";
import {
  ArrowUp, ArrowDown, Minus, ArrowUpDown, Eye, Search,
  Sword, Shield, Sparkles, Pill, Package, Scroll, Layers,
} from "lucide-react";
import { ElementBadge } from "./ElementBadge";
import ItemChips, { RequirementsChip } from "./ItemChips";

const RARITY_ORDER = { mythic: 6, legendary: 5, epic: 4, rare: 3, uncommon: 2, common: 1 };
const RARITY_COLOR = {
  common: "#8c8c8c", uncommon: "#6E8B3D", rare: "#4A7BC7",
  epic: "#A97AB0", legendary: "#E5C34A", mythic: "#E33333",
};
const EQUIP_SLOTS = ["weapon", "armor", "trinket"];
const SLOT_LABEL = {
  weapon: "Weapon", armor: "Armor", trinket: "Trinket",
  consumable: "Consumable", material: "Material", quest: "Quest Item",
  scroll: "Scroll",
};

// Category → predicate over item.slot.
const CATEGORIES = [
  { id: "all",         labelKey: "inventory.all",         Icon: Package,   test: () => true },
  { id: "weapon",      labelKey: "inventory.weapons",     Icon: Sword,     test: i => i.slot === "weapon" },
  { id: "armor",       labelKey: "inventory.armor",       Icon: Shield,    test: i => i.slot === "armor" },
  { id: "trinket",     labelKey: "inventory.trinkets",    Icon: Sparkles,  test: i => i.slot === "trinket" },
  { id: "consumable",  labelKey: "inventory.consumables", Icon: Pill,      test: i => i.slot === "consumable" },
  { id: "material",    labelKey: "inventory.materials",   Icon: Layers,    test: i => i.slot === "material" },
  { id: "quest",       labelKey: "inventory.quest",       Icon: Scroll,    test: i => i.slot === "quest" || i.slot === "scroll" },
];

const SORT_OPTIONS = [
  { id: "rarity",  labelKey: "inventory.rarity" },
  { id: "level",   labelKey: "inventory.level" },
  { id: "power",   labelKey: "inventory.power" },
  { id: "type",    labelKey: "inventory.type" },
  { id: "newest",  labelKey: "inventory.newest" },
  { id: "value",   labelKey: "inventory.value" },
];


// ── Utilities ─────────────────────────────────────────────────────

function effectiveBonus(item, heroClass) {
  const bonus = item?.stat_bonus || 0;
  if (item?.preferred_class && item.preferred_class === heroClass) return Math.floor(bonus * 1.5);
  return bonus;
}

function itemPower(item) {
  // Total spent points from stat_bonuses + elemental damage/resistance
  if (!item) return 0;
  const stats = item.stat_bonuses || (item.stat ? { [item.stat]: item.stat_bonus || 0 } : {});
  const statPow = Object.values(stats).reduce((a, b) => a + Number(b || 0), 0) * 8;
  const dmg = Object.values(item.elemental_damage || {}).reduce((a, b) => a + Number(b || 0), 0);
  const res = Object.values(item.elemental_resistance || {}).reduce((a, b) => a + Number(b || 0) * 40, 0);
  return statPow + dmg * 3 + res;
}


// ── Class Sets summary (replaces the old Workshop widget) ──────────

const CLASS_SETS = [
  { name: "Warrior",  stat: "STR", Icon: Sword },
  { name: "Guardian", stat: "DEF", Icon: Shield },
  { name: "Rogue",    stat: "DEX", Icon: Sparkles },
  { name: "Wizard",   stat: "INT", Icon: Package },
  { name: "Bard",     stat: "CHA", Icon: Scroll },
];

function ClassSetsSummary({ ch }) {
  const equipped = ch.equipped || {};
  const rows = CLASS_SETS.map(c => {
    const worn = EQUIP_SLOTS.filter(s => equipped[s]?.preferred_class === c.name).length;
    return { ...c, worn, complete: worn === 3 };
  }).filter(r => r.worn > 0);   // hide sets you have 0 pieces of
  if (rows.length === 0) return null;
  return (
    <div className="panel p-2" data-testid="inventory-sets-summary">
      <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c] mb-1">Class Sets</div>
      <div className="flex flex-wrap gap-2">
        {rows.map(r => {
          const Icon = r.Icon;
          return (
            <div key={r.name} className={`flex items-center gap-1.5 px-2 py-1 border font-mono text-[10px] uppercase tracking-widest
              ${r.complete ? "border-[#B89C3A] text-[#E5C34A]" : "border-[#333] text-[#8c8c8c]"}`}>
              <Icon size={11} strokeWidth={1.6} /> {r.name} {r.worn}/3 {r.complete && "· +3 " + r.stat}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Advanced compare modal (Session 5) ────────────────────────────

function DeltaRow({ label, delta, unit = "", positiveGood = true }) {
  if (delta === 0 || delta === undefined || delta === null) return null;
  const positive = delta > 0;
  const good = positiveGood ? positive : !positive;
  const color = good ? "#6E8B3D" : "#E33333";
  const Icon = positive ? ArrowUp : ArrowDown;
  const sign = positive ? "+" : "";
  return (
    <div className="flex items-center gap-1.5 font-mono text-[11px]" style={{ color }}>
      <Icon size={11} strokeWidth={2} />
      <span>{sign}{delta}{unit} {label}</span>
    </div>
  );
}

function collectStatBonuses(item) {
  if (!item) return {};
  if (item.stat_bonuses && typeof item.stat_bonuses === "object") return item.stat_bonuses;
  if (item.stat) return { [item.stat]: item.stat_bonus || 0 };
  return {};
}

function CompareModal({ item, equipped, heroClass, onClose }) {
  const newBonuses = collectStatBonuses(item);
  const curBonuses = collectStatBonuses(equipped);
  const allStats = new Set([...Object.keys(newBonuses), ...Object.keys(curBonuses)]);

  const newElDmg = item.elemental_damage || {};
  const curElDmg = equipped?.elemental_damage || {};
  const newElRes = item.elemental_resistance || {};
  const curElRes = equipped?.elemental_resistance || {};
  const allDmg = new Set([...Object.keys(newElDmg), ...Object.keys(curElDmg)]);
  const allRes = new Set([...Object.keys(newElRes), ...Object.keys(curElRes)]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="compare-modal">
      <div className="panel max-w-2xl w-full p-4 relative" onClick={e => e.stopPropagation()}>
        <div className="absolute -top-3 left-4 px-3 bg-[#050505] font-heading text-xs tracking-[0.3em] text-[#E5C34A]">COMPARE</div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <ItemPreviewCard label="Equipped" item={equipped} heroClass={heroClass} />
          <ItemPreviewCard label="Inventory" item={item} heroClass={heroClass} />
        </div>

        {/* Deltas */}
        <div className="panel-inner p-3 mt-3 space-y-1" data-testid="compare-deltas">
          <div className="font-heading text-[10px] tracking-widest uppercase text-[#B89C3A] mb-1">Δ Changes</div>

          {[...allStats].map(stat => {
            const cur = Number(curBonuses[stat] || 0);
            const nxt = Number(newBonuses[stat] || 0);
            return <DeltaRow key={stat} label={stat.toUpperCase()} delta={nxt - cur} />;
          })}

          {[...allDmg].map(el => {
            const cur = Number(curElDmg[el] || 0);
            const nxt = Number(newElDmg[el] || 0);
            const diff = nxt - cur;
            if (diff === 0) return null;
            return (
              <div key={"dmg-" + el} className="flex items-center gap-2">
                <ElementBadge element={el} size="sm" showLabel={false} />
                <DeltaRow label={`${el} Damage`} delta={diff} />
              </div>
            );
          })}

          {[...allRes].map(el => {
            const cur = Number(curElRes[el] || 0);
            const nxt = Number(newElRes[el] || 0);
            const diff = Math.round((nxt - cur) * 100);
            if (diff === 0) return null;
            return (
              <div key={"res-" + el} className="flex items-center gap-2">
                <ElementBadge element={el} size="sm" showLabel={false} />
                <DeltaRow label={`${el} Resistance`} delta={diff} unit="%" />
              </div>
            );
          })}

          {/* Value delta */}
          <DeltaRow label="Value" delta={Number(item.value || 0) - Number(equipped?.value || 0)} unit="g" positiveGood={true} />

          {allStats.size === 0 && allDmg.size === 0 && allRes.size === 0 && !equipped && (
            <div className="italic text-[#666] text-xs">No item currently equipped in this slot — anything is an upgrade.</div>
          )}
        </div>

        <button onClick={onClose} data-testid="compare-close"
          className="mt-4 w-full py-2 border border-[#333] text-[#8c8c8c] hover:border-[#E5C34A] hover:text-[#E5C34A] font-heading text-xs tracking-widest uppercase transition-colors">
          Close
        </button>
      </div>
    </div>
  );
}


function ItemPreviewCard({ label, item, heroClass }) {
  return (
    <div className="panel-inner p-3 min-h-[140px] flex flex-col">
      <div className="font-heading text-[10px] uppercase tracking-widest text-[#666] mb-1">{label}</div>
      {!item ? (
        <div className="italic text-xs text-[#666] my-auto text-center">Nothing</div>
      ) : (
        <>
          <div className="font-heading text-sm border-l-2 pl-2" style={{ color: RARITY_COLOR[item.rarity], borderLeftColor: RARITY_COLOR[item.rarity] }}>{item.name}</div>
          <div className="font-mono text-[10px] text-[#8c8c8c] mt-1 space-y-0.5">
            <div>{item.rarity} · {item.slot} · {item.value}g</div>
            {Object.entries(collectStatBonuses(item)).map(([s, v]) => (
              <div key={s} className="text-[#B0ADA5]">+{v} {s.toUpperCase()}</div>
            ))}
            {item.preferred_class && (
              <div className="text-[#B89C3A]">
                Class: {item.preferred_class}
                {item.preferred_class === heroClass && <span> ★</span>}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {Object.entries(item.elemental_damage || {}).map(([el, v]) => (
              <ElementBadge key={"d" + el} element={el} value={`+${v}`} size="sm" showLabel={false} />
            ))}
            {Object.entries(item.elemental_resistance || {}).map(([el, v]) => (
              <ElementBadge key={"r" + el} element={el} value={`${Math.round(v * 100)}%`} size="sm" showLabel={false} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}


// ── Inline delta chip on the row (unchanged intent) ────────────────

function ComparisonBadge({ item, equipped, heroClass }) {
  if (!EQUIP_SLOTS.includes(item.slot)) return null;
  const newEff = effectiveBonus(item, heroClass);
  const curEff = equipped ? (equipped.applied_bonus ?? effectiveBonus(equipped, heroClass)) : 0;
  if (!equipped) return <StatDelta color="#6E8B3D" Icon={ArrowUp} text={`+${newEff} ${item.stat || "stat"} (empty)`} />;
  const sameStat = equipped.stat === item.stat;
  if (!sameStat) return <StatDelta color="#B89C3A" Icon={Minus} text={`swap ${equipped.stat}→${item.stat}`} />;
  const delta = newEff - curEff;
  if (delta > 0) return <StatDelta color="#6E8B3D" Icon={ArrowUp} text={`+${delta} vs equipped`} />;
  if (delta < 0) return <StatDelta color="#E33333" Icon={ArrowDown} text={`${delta} vs equipped`} />;
  return <StatDelta color="#8c8c8c" Icon={Minus} text="same" />;
}

function StatDelta({ color, Icon, text }) {
  return (
    <div className="flex items-center gap-1 mt-1">
      <Icon className="w-3 h-3" style={{ color }} strokeWidth={2} />
      <span className="font-mono text-[10px]" style={{ color }}>{text}</span>
    </div>
  );
}


// ── Main panel ────────────────────────────────────────────────────

export default function InventoryPanel({ ch, setCh }) {
  const { t, i18n } = useTranslation();
  // Small closure so we can pass items through Gemini's translations table
  // without threading the language everywhere. Falls back to item.name.
  const itemName = (it) => localizedString(it, "name", i18n.language) || it.name;
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("rarity");
  const [compareItem, setCompareItem] = useState(null);

  const items = ch.inventory || [];
  const equipped = ch.equipped || {};

  const filtered = useMemo(() => {
    const cat = CATEGORIES.find(c => c.id === category) || CATEGORIES[0];
    let pool = items.filter(cat.test);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      pool = pool.filter(i => (i.name || "").toLowerCase().includes(q));
    }
    pool.sort((a, b) => {
      switch (sort) {
        case "rarity": return (RARITY_ORDER[b.rarity] || 0) - (RARITY_ORDER[a.rarity] || 0);
        case "value":  return (b.value || 0) - (a.value || 0);
        case "level":  return (b.level || 0) - (a.level || 0);
        case "power":  return itemPower(b) - itemPower(a);
        case "type":   return (a.slot || "").localeCompare(b.slot || "");
        case "newest": return (b.acquired_at || 0) - (a.acquired_at || 0);
        default: return 0;
      }
    });
    return pool;
  }, [items, category, query, sort]);

  const use = async (id) => {
    setBusy(true);
    try { setCh(await api.useItem(id)); toast("Consumed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const equip = async (id) => {
    setBusy(true);
    try { setCh(await api.equip(id)); toast.success("Equipped"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <ClassSetsSummary ch={ch} />

      {/* Category chip row */}
      <div className="panel p-2 flex flex-wrap gap-1" data-testid="inventory-categories">
        {CATEGORIES.map(c => {
          const Icon = c.Icon;
          const count = c.id === "all" ? items.length : items.filter(c.test).length;
          const active = category === c.id;
          return (
            <button key={c.id} onClick={() => setCategory(c.id)}
              data-testid={`inv-cat-${c.id}`}
              className={`px-2 py-1 flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase border transition-colors
                ${active ? "border-[#E5C34A] text-[#E5C34A] bg-[#B89C3A]/10" : "border-[#262626] text-[#8c8c8c] hover:text-[#B0ADA5]"}`}>
              <Icon size={11} strokeWidth={1.6} />
              {t(c.labelKey)}
              <span className="opacity-60">·{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search + Sort */}
      <div className="panel p-2 flex flex-wrap items-center gap-2" data-testid="inventory-search-sort">
        <div className="flex items-center gap-1 flex-1 min-w-[160px]">
          <Search className="w-3 h-3 text-[#666]" strokeWidth={1.5} />
          <input
            data-testid="inventory-search"
            type="text" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search by name..."
            className="flex-1 bg-[#0a0a0a] border border-[#262626] px-2 py-1 text-xs text-[#E2DFD8] font-mono placeholder:text-[#666]"
          />
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <ArrowUpDown className="w-3 h-3 text-[#8c8c8c]" strokeWidth={1.5} />
          {SORT_OPTIONS.map(s => (
            <button key={s.id} onClick={() => setSort(s.id)}
              data-testid={`sort-${s.id}`}
              className={`px-2 py-1 text-[10px] font-heading tracking-widest uppercase border transition-colors
                ${sort === s.id ? "border-[#B89C3A] text-[#E5C34A]" : "border-[#262626] text-[#8c8c8c] hover:text-[#B0ADA5]"}`}>
              {t(s.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Item list */}
      <div className="grid grid-cols-1 gap-1" data-testid="inventory-list">
        {filtered.length === 0 && (
          <div className="panel p-4 text-center italic text-[#666] text-sm">
            {query ? "No items match that name." : "Your pack is empty. The road provides — eventually."}
          </div>
        )}
        {filtered.map(item => (
          <div key={item.id} data-testid={`item-${item.id}`}
            className="panel-inner p-2 border-l-4"
            style={{ borderLeftColor: RARITY_COLOR[item.rarity] || "#333" }}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-heading text-sm truncate" style={{ color: RARITY_COLOR[item.rarity] }}>
                  {itemName(item)}
                </div>
                <div className="font-mono text-[10px] text-[#8c8c8c] uppercase tracking-wider">
                  {SLOT_LABEL[item.slot] || item.slot} · {item.rarity} · {item.value}g
                  {item.level ? ` · Lv${item.level}` : ""}
                  {item.stat_bonus > 0 && ` · +${item.stat_bonus} ${item.stat}`}
                  {item.heal > 0 && ` · heals ${item.heal}`}
                  {item.preferred_class && (
                    <span className="text-[#B89C3A]"> · {item.preferred_class}{item.preferred_class === ch.class && " ★"}</span>
                  )}
                </div>
                {/* Elemental affix chips + gm_effects (§9 Pack redesign) */}
                <ItemChips item={item} compact />
                {/* Legacy fallback for old items still using elemental maps
                    but no gm_effects: keep the ElementBadge row below. */}
                {(Object.keys(item.elemental_damage || {}).length === 0
                  && Object.keys(item.elemental_resistance || {}).length === 0
                  && !item.gm_effects?.length && !item.element && !item.resistance_element) && (
                  <RequirementsChip requirements={item.requirements} />
                )}
                <ComparisonBadge item={item} equipped={equipped[item.slot]} heroClass={ch.class} />
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {EQUIP_SLOTS.includes(item.slot) && (
                  <button data-testid={`compare-${item.id}`} onClick={() => setCompareItem(item)}
                    className="px-2 py-1 text-[10px] font-heading tracking-widest uppercase border border-[#333] text-[#8c8c8c] hover:border-[#E5C34A] hover:text-[#E5C34A] flex items-center gap-1 transition-colors">
                    <Eye className="w-3 h-3" strokeWidth={1.5} /> Compare
                  </button>
                )}
                <div className="flex gap-1">
                  {item.slot === "consumable" && (
                    <button data-testid={`use-${item.id}`} onClick={() => use(item.id)} disabled={busy}
                      className="px-2 py-1 text-[10px] font-heading tracking-widest uppercase border border-[#4A5D23] text-[#6E8B3D]">{t("inventory.use")}</button>
                  )}
                  {EQUIP_SLOTS.includes(item.slot) && (
                    <button data-testid={`equip-${item.id}`} onClick={() => equip(item.id)} disabled={busy}
                      className="px-2 py-1 text-[10px] font-heading tracking-widest uppercase border border-[#E55A00] text-[#E55A00]">{t("inventory.equip")}</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {compareItem && (
        <CompareModal
          item={compareItem}
          equipped={equipped[compareItem.slot]}
          heroClass={ch.class}
          onClose={() => setCompareItem(null)}
        />
      )}
    </div>
  );
}
